import {
  Component,
  createRef,
  memo,
  useMemo,
  type ReactNode,
  type ErrorInfo,
} from "react";
import ReactECharts from "echarts-for-react";
import "echarts/theme/v5.js";
import type { ChartSpec } from "../../types";
import { chartScreenSummary } from "./chartScreenSummary";
import { buildOption } from "./chartOptions";
import { sankeyHeight } from "./sankeyLayout";
import { useGoogleText } from "@/lib/translation/useGoogleText";
import {
  chartTextSources,
  translateChartSpec,
  SCROLL_HINT,
} from "./chartTranslation";

interface ChartRendererProps {
  spec: ChartSpec;
  isDark?: boolean;
}

interface ChartViewProps extends ChartRendererProps {
  saveLabel: string;
  chartLabel: string;
  scrollHint: string;
  translateNode: (text: string) => string;
}

interface ErrorState {
  hasError: boolean;
  width: number;
}

const ChartView = memo(
  class ChartView extends Component<ChartViewProps, ErrorState> {
    state: ErrorState = { hasError: false, width: 0 };
    private containerRef = createRef<HTMLDivElement>();
    private resizeObserver?: ResizeObserver;
    private optionCache?: {
      spec: ChartSpec;
      isDark: boolean;
      width: number;
      option: Record<string, unknown>;
    };

    private getOption(spec: ChartSpec, isDark: boolean): object {
      const width =
        spec.chartType === "sankey"
          ? Math.max(this.state.width, 640)
          : this.state.width || 600;
      const previous = this.optionCache;
      if (
        previous?.spec === spec &&
        previous.isDark === isDark &&
        previous.width === width
      ) {
        return previous.option;
      }
      const option = buildOption(
        spec,
        isDark,
        width,
        this.props.translateNode,
      ) as Record<string, unknown>;
      const toolbox = option.toolbox as
        { feature?: { saveAsImage?: { title: string } } } | undefined;
      if (toolbox?.feature?.saveAsImage)
        toolbox.feature.saveAsImage.title = this.props.saveLabel;
      // Resizing should update labels immediately without replaying the data animation.
      if (previous?.spec === spec && previous.isDark === isDark)
        option.animation = false;
      this.optionCache = { spec, isDark, width, option };
      return option;
    }

    componentDidMount(): void {
      const container = this.containerRef.current;
      if (!container) return;
      this.resizeObserver = new ResizeObserver(([entry]) => {
        const width = Math.round(entry.contentRect.width);
        if (width > 0 && width !== this.state.width) this.setState({ width });
      });
      this.resizeObserver.observe(container);
    }

    componentWillUnmount(): void {
      this.resizeObserver?.disconnect();
    }

    static getDerivedStateFromError(): Pick<ErrorState, "hasError"> {
      return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
      console.warn("Chart render error:", error.message, info.componentStack);
    }

    render(): ReactNode {
      if (this.state.hasError) {
        return (
          <div className="p-4 text-sm text-muted-foreground bg-muted rounded-sm border border-border text-center">
            Chart could not be rendered
          </div>
        );
      }

      const { spec, isDark } = this.props;

      if (!spec.data.datasets?.length && !spec.data.links?.length) {
        return (
          <div className="p-4 text-sm text-muted-foreground bg-muted rounded-sm border border-border text-center">
            No data available for chart
          </div>
        );
      }

      const option = this.getOption(spec, isDark ?? false);
      const summary = chartScreenSummary(spec);
      const isSankey = spec.chartType === "sankey";

      return (
        <div
          ref={this.containerRef}
          className="notranslate w-full min-w-0"
          translate="no"
          data-ai-chart-summary={summary.text}
          data-ai-context-truncated={summary.truncated ? "true" : undefined}
          aria-label={`${this.props.chartLabel} ${spec.title}`}
          role={isSankey ? "region" : undefined}
          tabIndex={isSankey ? 0 : undefined}
          style={isSankey ? { overflowX: "auto" } : undefined}
        >
          {isSankey && this.state.width > 0 && this.state.width < 640 && (
            <p className="mb-2 text-xs text-muted-foreground">
              {this.props.scrollHint}
            </p>
          )}
          <ReactECharts
            option={option}
            theme="v5"
            notMerge={true}
            style={{
              height: isSankey
                ? sankeyHeight(spec.data.links ?? [], spec.height)
                : (spec.height ?? 350),
              minWidth: isSankey ? 640 : undefined,
            }}
          />
        </div>
      );
    }
  },
);

export const ChartRenderer = memo(function ChartRenderer({
  spec,
  isDark,
}: ChartRendererProps) {
  const sources = useMemo(() => chartTextSources(spec), [spec]);
  const translate = useGoogleText(sources);
  const translatedSpec = useMemo(
    () => translateChartSpec(spec, translate),
    [spec, translate],
  );
  return (
    <ChartView
      spec={translatedSpec}
      isDark={isDark}
      saveLabel={translate("Save as PNG")}
      chartLabel={translate("Chart data:")}
      scrollHint={translate(SCROLL_HINT)}
      translateNode={translate}
    />
  );
});
